import React from 'react';
import { Microscope, Cpu, HeartPulse, Building2, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Research = () => {
  const interests = [
    {
      icon: <Cpu size={48} />,
      title: 'Computational Solid Mechanics',
      description: 'Advanced numerical methods including Element-Free Galerkin Method, Extended Finite Element Method (XFEM), and meshfree methods for solving complex elasticity problems and fracture mechanics.',
      color: 'from-blue-600 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
    },
    {
      icon: <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_research-profile-10/artifacts/ef79a2ad1f2faec6_layers%20%281%29.png" alt="Composites" className="w-12 h-12" />,
      title: 'Mechanics of Composites',
      description: 'Manufacturing, testing, and computational modeling of fiber-reinforced composites including carbon/glass fiber materials, delamination studies, cohesive zone modeling, and meta-sandwich structures.',
      color: 'from-cyan-500 to-purple-500',
      bgColor: 'bg-gradient-to-br from-cyan-50 to-purple-50',
    },
    {
      icon: <HeartPulse size={48} />,
      title: 'Fracture Mechanics & Medical Device Innovation',
      description: 'Advanced fracture mechanics research and design of innovative medical devices including prosthetics, orthotic devices, ventilators for respiratory emergencies, and biomedical implants with focus on indigenous solutions.',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-gradient-to-br from-purple-50 to-pink-50',
    },
  ];

  const collaborators = [
    { name: 'DST-SERB', color: 'from-blue-600 to-cyan-500' },
    { name: 'VSSC ISRO', color: 'from-cyan-500 to-blue-600' },
    { name: 'TATA Materials', color: 'from-purple-600 to-pink-500' },
    { name: 'SABIC', color: 'from-pink-500 to-orange-500' },
    { name: 'DRDO', color: 'from-orange-500 to-amber-500' },
    { name: 'IOCL', color: 'from-amber-500 to-yellow-500' },
    { name: 'Robert Bosch', color: 'from-green-500 to-teal-500' },
  ];

  return (
    <section id="research" className="section-container bg-gradient-to-br from-white via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-float"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-full filter blur-3xl opacity-20"></div>
      
      <h2 className="section-title">Research Interests</h2>
      
      <div className="grid md:grid-cols-3 gap-8 mb-16 relative z-10">
        {interests.map((interest, index) => (
          <Card key={index} className="border-2 border-transparent hover:border-purple-200 overflow-hidden group hover-lift">
            <div className={`h-2 bg-gradient-to-r ${interest.color}`}></div>
            <CardContent className={`p-8 ${interest.bgColor} relative`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full filter blur-2xl opacity-50"></div>
              <div className={`inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br ${interest.color} rounded-2xl text-white mb-6 shadow-xl transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 relative z-10`}>
                {interest.icon}
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                {interest.title}
              </h3>
              <p className="text-slate-700 leading-relaxed">
                {interest.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl p-10 shadow-2xl border-2 border-blue-100 relative overflow-hidden z-10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-200 to-purple-200 rounded-full filter blur-3xl opacity-30"></div>
        
        <div className="flex items-center justify-center gap-3 mb-8 relative z-10">
          <Building2 size={32} className="text-blue-600" />
          <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Industry Collaborators & Supporters
          </h3>
          <Sparkles size={32} className="text-purple-600" />
        </div>
        
        <div className="flex flex-wrap justify-center gap-4 relative z-10">
          {collaborators.map((collaborator, index) => (
            <span
              key={index}
              className={`px-6 py-3 bg-gradient-to-r ${collaborator.color} text-white rounded-full font-semibold shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-rotate-2 transition-all duration-300 cursor-pointer`}
            >
              {collaborator.name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Research;